/**
 * GxCountryDropdown
 *
 * A fully controlled, reusable country selector with autocomplete, flag display,
 * keyboard navigation, and built-in validation. No Redux dependency — all state
 * is driven by props from the parent.
 *
 * ── Features ──────────────────────────────────────────────────────────────────
 * - Opens with the full country list on focus (not filtered to current value).
 * - Filters the list as the user types (starts-with match, case-insensitive).
 * - Displays the selected country's flag using data from `fetchCountries()`.
 * - Falls back to a default SVG icon if no flag data is found or image fails.
 * - Keyboard navigation: ArrowUp / ArrowDown to move, Enter to select, Escape to close.
 * - Clear button resets the selection and restores the full list on next open.
 * - Validation fires on blur, on external `triggerValidation`, or via `validationMessage`.
 * - `onValidate` callback notifies the parent whether the current value is valid.
 *
 * ── Usage ─────────────────────────────────────────────────────────────────────
 * <GxCountryDropdown
 *   countries={countryNames}           // string[] — full list from parent (e.g. gxSmeGlobalCountryApi)
 *   value={selectedCountryName}        // controlled value — country name string
 *   onChange={handleCountryChange}     // called with the new country name string
 *   label="Select Country"
 *   fieldName="country"
 *   onValidate={(field, isValid) => {}}
 *   triggerValidation={submitAttempted}
 *   disable={disableOnRole}
 * />
 *
 * ── Props ──────────────────────────────────────────────────────────────────────
 * @prop countries         string[]   Full list of country names to display in the dropdown.
 * @prop label             string     Label text shown above the input. Default: "select country".
 * @prop value             string     Controlled value — the currently selected country name.
 * @prop onChange          function   Called with the new value on every input change or selection.
 * @prop validationMessage string     External error message pushed from the parent (e.g. form-level validation).
 * @prop starlable         boolean    Whether to show the required (*) star indicator. Default: true.
 * @prop disable           boolean    Disables the input when true. Default: false.
 * @prop fieldName         string     Identifier passed to onValidate to distinguish fields in multi-field forms.
 * @prop triggerValidation boolean    When flipped to true, forces validation to run (e.g. on form submit).
 * @prop onValidate        function   Called with (fieldName, isValid) whenever validation state changes.
 */

import { useEffect, useRef, useState } from "react";
import { ICON_BASE_PATH, COUNTRY_BASE_PATH } from "../../../../utils/config";
import { useTranslation } from "react-i18next";
import {
  fetchCountries,
  type Country,
} from "@/app/services/localservices/countryService";

interface GxCountryDropdownProps {
  countries: string[];
  label?: string;
  value: string;
  onChange: (value: string) => void;
  validationMessage?: string;
  starlable?: boolean;
  disable?: boolean;
  fieldName?: string;
  triggerValidation?: boolean;
  onValidate?: (field: string, isValid: boolean) => void;
}

/**
 * Resolves the full flag image URL for a given Country object.
 * - If flagIcon is already an absolute URL or root-relative path, use it as-is.
 * - Otherwise, prepend COUNTRY_BASE_PATH (e.g. "/assets/flags/").
 * - If no flagIcon is present, returns a 1×1 transparent GIF as a safe fallback.
 */
const getFlagUrl = (country: Country): string => {
  if (country.flagIcon) {
    if (
      country.flagIcon.startsWith("http://") ||
      country.flagIcon.startsWith("https://") ||
      country.flagIcon.startsWith("/")
    ) {
      return country.flagIcon;
    }
    return `${COUNTRY_BASE_PATH}${country.flagIcon}`;
  }
  return "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
};

const GxCountryDropdown: React.FC<GxCountryDropdownProps> = ({
  countries,
  label = "select country",
  value,
  onChange,
  validationMessage,
  starlable = true,
  disable = false,
  fieldName = "beneficiaryBankCountry",
  triggerValidation = false,
  onValidate,
}) => {
  const { t } = useTranslation();

  // ── Local state ────────────────────────────────────────────────────────────
  const [error, setError] = useState<string>("");
  const [touched, setTouched] = useState(false); // true after first blur or selection
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredCountries, setFilteredCountries] = useState<string[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1); // keyboard-highlighted index in the list
  const [countryDataList, setCountryDataList] = useState<Country[]>([]); // flag data from countryService

  // ── Refs ───────────────────────────────────────────────────────────────────

  /**
   * isTypingRef — true only while the user is actively typing characters.
   * Used to distinguish "user is searching" from "field has a pre-selected value".
   * Prevents handleFocus from filtering on open when a country is already selected.
   */
  const isTypingRef = useRef(false);

  /**
   * suggestionClickedRef — set to true on mouseDown of a list item.
   * Prevents the input's onBlur from closing the dropdown before handleSelect fires.
   * Cleared 100ms after selection to restore normal blur behavior.
   */
  const suggestionClickedRef = useRef(false);

  /** Holds the blur debounce timer so it can be cancelled on mouseDown. */
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Ref to the dropdown list container — used to scroll highlighted items into view. */
  const listRef = useRef<HTMLDivElement | null>(null);

  /** Ref to the text input — reserved for programmatic focus if needed. */
  const inputRef = useRef<HTMLInputElement | null>(null);

  // ── Load flag data ─────────────────────────────────────────────────────────
  // Fetched once on mount from countryService (local JSON / cached).
  // Used only for flag icon resolution — not for the dropdown list itself.
  useEffect(() => {
    fetchCountries()
      .then(setCountryDataList)
      .catch(() => setCountryDataList([]));
  }, []);

  // ── Helpers ────────────────────────────────────────────────────────────────

  /**
   * Looks up the Country object by name (case-insensitive) from the loaded flag data.
   * Returns undefined if not found — callers render a placeholder in that case.
   */
  const getCountryDataByName = (name: string): Country | undefined => {
    return countryDataList.find(
      (c) => c.name.toLowerCase() === name.toLowerCase(),
    );
  };

  /** Country object for the currently selected value — drives the flag shown in the input. */
  const selectedCountryData = value ? getCountryDataByName(value) : undefined;

  // ── Validation ─────────────────────────────────────────────────────────────

  /**
   * Returns an error string for the given value, or "" if valid.
   * - Empty value: required error.
   * - Value not in the countries list with no partial match: invalid selection error.
   * - Partial match exists (user is still typing): no error yet.
   */
  const validate = (val: string): string => {
    if (!val) return `${t("please_select_the")} ${label}.`;
    if (touched && val && !countries.includes(val)) {
      const hasPartialMatch = countries.some((country) =>
        country.toLowerCase().startsWith(val.toLowerCase()),
      );
      if (!hasPartialMatch) {
        return `${t("please_select_valid")} ${label}.`;
      }
    }
    return "";
  };

  /**
   * Runs validate(), updates local error state, and notifies the parent
   * via onValidate whether the current value is a valid country selection.
   */
  const updateValidation = (val: string) => {
    const newError = validate(val);
    setError(newError);
    const isValid = countries.includes(val);
    onValidate?.(fieldName, isValid);
    return newError;
  };

  // ── Event handlers ─────────────────────────────────────────────────────────

  /**
   * handleInputChange — fires on every keystroke.
   * Marks isTypingRef as true and filters the list to entries starting with the typed value.
   * Shows the full list if the input is cleared.
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value;
    isTypingRef.current = true;
    onChange(newVal);
    setActiveIndex(-1);

    const filtered = newVal
      ? countries.filter((c) =>
          c.toLowerCase().startsWith(newVal.toLowerCase()),
        )
      : countries;
    setFilteredCountries(filtered);
    setShowSuggestions(true);

    // Clear inline error as soon as there's a partial match, to avoid
    // showing "invalid" while the user is still typing toward a valid value.
    if (touched && error) {
      const hasPartialMatch = filtered.length > 0;
      if (hasPartialMatch || !newVal) setError("");
      else setError(validate(newVal));
    }
  };

  /**
   * handleSelect — called when the user clicks or keyboard-confirms a list item.
   * - Cancels the blur debounce (mouseDown fires before blur).
   * - Resets typing state so the next focus shows the full list again.
   * - Notifies parent with the selected country name and marks validation as valid.
   */
  const handleSelect = (countryName: string) => {
    isTypingRef.current = false;
    suggestionClickedRef.current = true;
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    onChange(countryName);
    setShowSuggestions(false);
    setActiveIndex(-1);
    setError("");
    setTouched(true);
    onValidate?.(fieldName, true);
    setTimeout(() => {
      suggestionClickedRef.current = false;
    }, 100);
  };

  /**
   * handleFocus — opens the dropdown when the input is focused.
   * Always shows the FULL country list regardless of the current value.
   * Filtering only happens while the user is actively typing (handleInputChange).
   *
   * FIX: Previously filtered by `value` on focus, which caused only the already-selected
   * country to appear in the list. Now the full list is always shown on open.
   */
  const handleFocus = () => {
    setFilteredCountries(countries);
    setShowSuggestions(true);
    isTypingRef.current = false;
  };

  /**
   * handleBlur — fires when the input loses focus.
   * Debounced by 300ms so that mouseDown on a list item can cancel it before
   * the dropdown closes. Runs validation and marks the field as touched.
   */
  const handleBlur = () => {
    if (suggestionClickedRef.current) return;
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    blurTimeoutRef.current = setTimeout(() => {
      setShowSuggestions(false);
      setActiveIndex(-1);
      setTouched(true);
      isTypingRef.current = false;
      updateValidation(value);
    }, 300);
  };

  /**
   * handleKeyDown — keyboard navigation within the open dropdown.
   * ArrowDown / ArrowUp  : move highlight and scroll into view.
   * Enter                : select highlighted item, or auto-select if only one match.
   * Escape               : close the dropdown without selecting.
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || filteredCountries.length === 0) return;
    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        const next = Math.min(activeIndex + 1, filteredCountries.length - 1);
        setActiveIndex(next);
        listRef.current
          ?.querySelectorAll<HTMLDivElement>("[data-item]")
          [next]?.scrollIntoView({ block: "nearest" });
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        const prev = Math.max(activeIndex - 1, 0);
        setActiveIndex(prev);
        listRef.current
          ?.querySelectorAll<HTMLDivElement>("[data-item]")
          [prev]?.scrollIntoView({ block: "nearest" });
        break;
      }
      case "Enter": {
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < filteredCountries.length) {
          handleSelect(filteredCountries[activeIndex]);
        } else if (filteredCountries.length === 1) {
          handleSelect(filteredCountries[0]);
        } else {
          setShowSuggestions(false);
        }
        break;
      }
      case "Escape":
        setShowSuggestions(false);
        setActiveIndex(-1);
        break;
    }
  };

  // ── Effects ────────────────────────────────────────────────────────────────

  /** Allows the parent to force validation (e.g. on form submit attempt). */
  useEffect(() => {
    if (triggerValidation) {
      setTouched(true);
      updateValidation(value);
    }
  }, [triggerValidation]);

  /** Syncs externally pushed validationMessage into local error state. */
  useEffect(() => {
    if (validationMessage) {
      setError(validationMessage);
      setTouched(true);
      onValidate?.(fieldName, false);
    } else if (touched) {
      updateValidation(value);
    }
  }, [validationMessage]);

  /** Cleanup: clear the blur debounce timer on unmount to prevent memory leaks. */
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  // ── Derived ────────────────────────────────────────────────────────────────

  /** true only when there is an active error AND the field has been touched. */
  const isInvalid = !!error && touched;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="gx_input_container">
      {/* Floating label — rises when a value is present */}
      <label
        className={`input-label gx-font-semibold ${value ? "floating" : ""}`}
      >
        {t(label)}
        {starlable && (
          <span
            className={`gx_input_star ${
              value ? "gx_input_star_float" : "gx_input_star_normal"
            }`}
          >
            *
          </span>
        )}
      </label>

      <div
        className={`gx_input_cont ${isInvalid ? "invalid" : "valid"} ${
          disable ? "gx-input-disabled" : ""
        }`}
        
      >
        <div className="gx-country-flag_name">
          {/* Flag icon: shows selected country's flag if found, otherwise default SVG */}
          {selectedCountryData ? (
            <img
              src={getFlagUrl(selectedCountryData)}
              alt={selectedCountryData.name}
              className={`${
                selectedCountryData
                  ? "gx-country-flag-selected"
                  : "gx-country-flag"
              } `}
              onError={(e) => {
                // Fallback to default icon if the flag image fails to load
                e.currentTarget.src = `${ICON_BASE_PATH}beneficiarybankcountry.svg`;
              }}
            />
          ) : (
            <img
              src={`${ICON_BASE_PATH}beneficiarybankcountry.svg`}
              alt="beneficiarybankcountry"
            />
          )}

          {/* Text input — drives both the display value and the autocomplete filter */}
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={disable}
            placeholder=""
            className={`gx-textbox-css ${isInvalid ? "gx-invalid" : "gx-valid"} ${
              disable ? "" : ""
            }`}
            aria-invalid={isInvalid}
          />

          {value && !disable && (
            <button
              type="button"
              aria-label="Clear selection"
              onClick={() => {
                isTypingRef.current = false;
                onChange("");
              }}
              className="gx-bene-dropdown-clear-btn"
            >
              <img src={`${ICON_BASE_PATH}cross.svg`} alt="Clear" />
            </button>
          )}
        </div>

        {/* Dropdown list — rendered only when open and there are matching results */}
        {showSuggestions && filteredCountries.length > 0 && (
          <div className="gx-bene-currency-filter" ref={listRef}>
            {filteredCountries.map((countryName, index) => {
              const countryData = getCountryDataByName(countryName);
              return (
                <div
                  key={countryName}
                  data-item
                  // onMouseDown (not onClick) so it fires before the input's onBlur,
                  // allowing handleSelect to cancel the blur debounce timer.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(countryName);
                  }}
                  className={`gx-country-item gx-country-row ${
                    index === activeIndex ? "bg-blue-50" : "hover:bg-gray-100"
                  }`}
                >
                  {/* Flag per row — hidden (not removed) if image fails to load */}
                  {countryData ? (
                    <img
                      src={getFlagUrl(countryData)}
                      alt={countryName}
                      className="gx-flag-img"
                      onError={(e) => {
                        e.currentTarget.style.visibility = "hidden";
                      }}
                    />
                  ) : (
                    <span className="gx-flag-placeholder" />
                  )}
                  <span className="gx-p12-m">{countryName}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* No-match message — shown when user has typed something but nothing matches */}
        {showSuggestions &&
          filteredCountries.length === 0 &&
          value.length > 0 && (
            <div className="gx-bene-currency-no_match">
              <p className="text-sm text-gray-500">
                No matching countries found
              </p>
            </div>
          )}
      </div>

      {/* Inline validation error — only shown after the field has been touched */}
      {isInvalid && (
        <div className="gx_input_validationmessage gx-font-regular">
          {error}
        </div>
      )}
    </div>
  );
};

export default GxCountryDropdown;


